import React, { useRef, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ShipeeziNewGrid, { GeneralActions, RowActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { NotificationToast } from "../../../../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../../../../context/StateContext";
import { MemberTimesheet } from "../../../../../../../../../../models/members/member.timesheet";
import { Filters, Searches } from "../../../../../../../../../../models/shipeezi.table";
import { TaskType } from "../../../../../../../../../../models/task.type";
import TasksTypesService from "../../../../../../../../../../services/task.type.service";
import TimesheetService from "../../../../../../../../../../services/timesheet.service";
import { CACHE_TIME, STALE_TIME } from "../../../../../../../../../../util/constants";
import { Status } from "../../../../../../../../../../util/enums";
import getRelatedSearch from "../../../../../../../../../../util/getRelatedSearch";
import { TaskTypeKeys, TimesheetKeys } from "../../../../../../../../../../util/queries";
import { useActions } from "../../../hooks/actions";
import { useTimesheet } from "../../../hooks/useTimesheet";
import { NewRecordButton } from "../../NewRecordButton";
import { TimesheetHeader } from "./components/Header";
import ManageTimesheeet, { ManageTimesheeetHandles } from "./ManageTimesheet";

export const TimesheetTab: React.FC = () => {
  const { t: translate } = useTranslation();
  const { location } = useHistory<{ isView?: boolean }>();

  const { member } = useActions();

  const queryClient = useQueryClient();

  const manageTimesheetRef = useRef<ManageTimesheeetHandles>(null);

  const { currentUser } = useAppState();

  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const {
    refetch,
    data: timesheetList,
    isFetching,
  } = useQuery({
    queryKey: TimesheetKeys.getAll([page, pageSize, filters, searches]),
    queryFn: async () => {
      const response = await TimesheetService.getAll({
        page,
        pageSize,
        memberId: member?.id,
        include: JSON.stringify([
          {
            association: "member",
            reference: ["id", "fullName", "name", "lastName"],
          },
          {
            association: "task",
            where: filters || {},
            include: [
              {
                association: "job",
                reference: ["id", "reference"],
                required: !!getRelatedSearch(searches, "reference"),
                where: getRelatedSearch(searches, "reference", undefined, true),
              },
              {
                association: "team",
                attributes: ["id", "name"],
                required: !!getRelatedSearch(searches, "teamName"),
                where: getRelatedSearch(searches, "teamName", "name", true),
              },
            ],
            required: !!(
              getRelatedSearch(searches, "reference") ||
              getRelatedSearch(searches, "teamName") ||
              filters?.type?.length
            ),
          },
        ]),
      });

      if (response?.data?.length && Array.isArray(response.data)) return response.data;

      return [];
    },
    enabled: !!member?.id,
    refetchOnWindowFocus: false,
  });

  const { refetch: refetchCount, data: timesheetCount } = useQuery({
    queryKey: TimesheetKeys.count([filters, searches]),
    queryFn: async () => {
      const response = await TimesheetService.count({
        memberId: member?.id,
        include: JSON.stringify([
          {
            association: "member",
          },
          {
            association: "task",
            where: filters || {},
            include: [
              {
                association: "job",
                required: !!getRelatedSearch(searches, "reference"),
                where: getRelatedSearch(searches, "reference", undefined, true),
              },
              {
                association: "team",
                required: !!getRelatedSearch(searches, "teamName"),
                where: getRelatedSearch(searches, "teamName", "name", true),
              },
            ],
            required: !!(
              getRelatedSearch(searches, "reference") ||
              getRelatedSearch(searches, "teamName") ||
              filters?.type?.length
            ),
          },
        ]),
      });

      if (response?.data !== undefined || response?.data !== null) return response.data;

      return 0;
    },
    enabled: !!member?.id,
    refetchOnWindowFocus: false,
  });

  const { data: taskTypes = [] } = useQuery({
    queryKey: TaskTypeKeys.getAll([]),
    queryFn: async () => {
      const response = await TasksTypesService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      });

      if (response?.data?.length) return response.data as TaskType[];

      return [];
    },
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
  });

  const { mutate: onCreateTimesheet, isLoading: isCreating } = useMutation({
    mutationKey: TimesheetKeys.create(),
    mutationFn: async (timesheet: MemberTimesheet) =>
      TimesheetService.create({ ...timesheet, memberId: member?.id }),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      queryClient.invalidateQueries({ queryKey: TimesheetKeys.getAll([]) });
      refetch();
      refetchCount();
      refetchWidgets();
      manageTimesheetRef.current?.close();
    },
    onError: (error) => {
      NotificationToast.error(translate(`${error}`));
      console.error(error);
    },
  });

  const { mutate: onUpdatingTimesheet, isLoading: isUpdating } = useMutation({
    mutationKey: TimesheetKeys.create(),
    mutationFn: async (timesheet: MemberTimesheet) =>
      TimesheetService.update(Number(timesheet.id), timesheet),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      queryClient.invalidateQueries({ queryKey: TimesheetKeys.getAll([]) });
      refetch();
      refetchCount();
      refetchWidgets();
      manageTimesheetRef.current?.close();
    },
    onError: (error) => {
      NotificationToast.error(translate("operationFailed"));
      console.error(error);
    },
  });
  const { timesheetGridColumns } = useTimesheet();

  const { data: timesheetWidgets, refetch: refetchWidgets } = useQuery({
    queryKey: TimesheetKeys.getWidgets([filters, searches]),
    queryFn: async () => {
      const response = await TimesheetService.getWidgets({
        memberId: member?.id,
        include: JSON.stringify([
          {
            attributes: [],
            association: "task",
            where: filters || {},
            include: [
              {
                attributes: [],
                association: "job",
                required: !!getRelatedSearch(searches, "reference"),
                where: getRelatedSearch(searches, "reference", undefined, true),
              },
              {
                attributes: [],
                association: "team",
                required: !!getRelatedSearch(searches, "teamName"),
                where: getRelatedSearch(searches, "teamName", "name", true),
              },
            ],
            required: !!(
              getRelatedSearch(searches, "reference") ||
              getRelatedSearch(searches, "teamName") ||
              filters?.type?.length
            ),
          },
        ]),
      });

      if (response?.data !== undefined || response?.data !== null) return response.data;

      return {
        workHours: null,
        breakHours: null,
        totalTimeHours: null,
        mostCommonTask: null,
      };
    },
    enabled: !!member?.id,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
  });

  const handleSearch = (searches: Searches) => {
    setSearches(searches);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys && filtersKeys?.length ? value : undefined);
  };

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "newRecord"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          column: "type",
          name: translate("task"),
          options:
            taskTypes && taskTypes?.length > 0
              ? taskTypes?.map((taskType) => {
                  return taskType.name;
                })
              : [],
          optionsSelected: filters?.type || [],
        },
      ],
      onChange: handleFilter,
    },
    newRecord: location?.state?.isView ? undefined : (
      <NewRecordButton
        label={translate("newTime")}
        onClick={() => manageTimesheetRef.current?.open()}
      />
    ),
  };

  const rowActions: RowActions = {
    handleEdit(_, row) {
      manageTimesheetRef.current?.open(row);
    },
    handleDetails(rowId, row) {
      manageTimesheetRef.current?.open(row, true);
    },
  };

  const onSubmitTimesheet = (data: MemberTimesheet) => {
    if (data?.id) {
      onUpdatingTimesheet(data);
    } else {
      onCreateTimesheet(data);
    }
  };

  return (
    <>
      <TimesheetHeader timesheetWidgets={timesheetWidgets} />
      <ShipeeziNewGrid
        loadingData={isFetching}
        data={timesheetList || []}
        totalItems={timesheetCount || 0}
        pageSize={pageSize}
        columns={timesheetGridColumns}
        showSelection={false}
        showSort={false}
        currentPage={page}
        pageSizeList={[10, 20, 40, 60]}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        positionActionsColumn="right"
        generalActions={generalActions}
        rowActions={rowActions}
        keyExtractor={(data) => data?.id?.toString() || data?.randomUuid}
        emptyData={translate("noInformationToBeShown")}
      />

      <ManageTimesheeet
        ref={manageTimesheetRef}
        isLoading={isCreating || isUpdating}
        onSubmitTimesheet={(data) => {
          if (data) {
            onSubmitTimesheet(data);
          }
        }}
      />
    </>
  );
};
